import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  DevicePhoneMobileIcon,
  BuildingLibraryIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

// ─── HERO SLIDER IMAGES ───────────────────────────────────────────────────────
import entranceGymPic from '../../assets/images/Entrance Gym pic.png';
import pcfStudioOut from '../../assets/images/pcf studio out.png';
import saunaPic from '../../assets/images/sauna pic.png';
import fitneseqp12 from '../../assets/images/fitneseqp12.png';

// ─── COMPANY PROFILE PDF ──────────────────────────────────────────────────────
import companyProfilePDF from '../../assets/images/PowerCore_Fitness_Profile.pdf';

const HERO_IMAGES = [entranceGymPic, pcfStudioOut, saunaPic, fitneseqp12];

// ─── Copy-to-clipboard helper field ───────────────────────────────────────────
const CopyField = ({ label, value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // Clipboard API unavailable — fail silently
    }
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
      <div>
        <p className="text-[11px] uppercase tracking-widest text-gray-400 font-medium mb-0.5">
          {label}
        </p>
        <p className="text-sm font-bold text-gray-900">{value}</p>
      </div>
      <button
        onClick={handleCopy}
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
        style={{ background: copied ? '#800020' : 'rgba(128,0,32,0.08)' }}
        aria-label={`Copy ${label}`}
        type="button"
      >
        {copied ? (
          <CheckIcon className="h-4 w-4 text-white" />
        ) : (
          <ClipboardDocumentIcon className="h-4 w-4" style={{ color: '#800020' }} />
        )}
      </button>
    </div>
  );
};

// ─── MINIMAL HERO SLIDER (no arrows, no dots, max-height 350px) ──────────────
const PortfolioHero = ({ images }) => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setCurrent((p) => (p + 1) % images.length);
    }, 4000);
    return () => clearInterval(t);
  }, [images.length]);

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ height: '350px', maxHeight: '350px' }}
    >
      {images.map((img, i) => (
        <img
          key={i}
          src={img}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] ease-in-out"
          style={{ opacity: i === current ? 1 : 0 }}
        />
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>
      <div
        className="absolute inset-0 opacity-40"
        style={{
          background: 'linear-gradient(135deg, rgba(128,0,32,0.35) 0%, transparent 60%)',
        }}
      ></div>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-[11px] font-bold tracking-[0.3em] uppercase px-4 py-1.5 rounded-full mb-4 bg-white/15 text-white backdrop-blur-sm"
        >
          Powercore Fitness
        </motion.span>
        <motion.h1
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-3xl md:text-5xl font-black text-white leading-none"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '-0.02em' }}
        >
          OUR <span style={{ color: '#ff6b8a' }}>PORTFOLIO</span>
        </motion.h1>
      </div>
    </section>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const QuickStart = () => {
  return (
    <div className="min-h-screen bg-white">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');
        body { font-family: 'DM Sans', sans-serif; }
      `}</style>

      <PortfolioHero images={HERO_IMAGES} />

      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container-custom max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span
              className="text-xs font-bold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full inline-block mb-3"
              style={{ background: 'rgba(128,0,32,0.08)', color: '#800020' }}
            >
              Payment Details
            </span>
            <h2
              className="text-3xl md:text-4xl font-black text-gray-900 leading-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '-0.02em' }}
            >
              WAYS TO <span style={{ color: '#800020' }}>PAY</span>
            </h2>
            <p className="text-sm text-gray-500 mt-3 max-w-md mx-auto">
              Tap to copy any field. Please use your name as the payment reference.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-7 border border-gray-100 shadow-sm hover:shadow-lg transition-all"
            >
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(128,0,32,0.08)' }}
                >
                  <DevicePhoneMobileIcon className="h-5 w-5" style={{ color: '#800020' }} />
                </div>
                <div>
                  <h3
                    className="font-black text-gray-900 text-lg leading-none"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    M-PESA PAYMENT
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">Lipa na M-Pesa, Paybill</p>
                </div>
              </div>
              <div>
                <CopyField label="Paybill Number" value="516600" />
                <CopyField label="Account Number" value="992299" />
                <CopyField label="Account Name" value="Powercore Fitness" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-7 border border-gray-100 shadow-sm hover:shadow-lg transition-all"
            >
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(128,0,32,0.08)' }}
                >
                  <BuildingLibraryIcon className="h-5 w-5" style={{ color: '#800020' }} />
                </div>
                <div>
                  <h3
                    className="font-black text-gray-900 text-lg leading-none"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    BANK PAYMENT
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">Diamond Trust Bank (DTB)</p>
                </div>
              </div>
              <div>
                <CopyField label="Account Number" value="0554049001" />
                <CopyField label="Account Name" value="POWERCORE FITNESS" />
                <CopyField label="Swift Code" value="DDTKEKENA" />
                <CopyField label="Branch" value="Imaara Mall Branch" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section
        className="py-16 md:py-20 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #800020 0%, #3d0010 100%)' }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
        </div>
        <div className="container-custom max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 bg-white/15 backdrop-blur-sm">
              <DocumentTextIcon className="h-7 w-7 text-white" />
            </div>
            <span className="text-xs font-bold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full inline-block mb-4 bg-white/20 text-white/80">
              Full Company Profile
            </span>
            <h2
              className="text-3xl md:text-4xl font-black text-white mb-4 leading-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              GET OUR COMPLETE <span className="text-yellow-400">BROCHURE</span>
            </h2>
            <p className="text-white/70 mb-8 max-w-lg mx-auto text-sm leading-relaxed">
              Pricing, membership plans, class schedules and everything else about PowerCore
              Fitness — all in one downloadable PDF.
            </p>
            <a
              href={companyProfilePDF}
              download="PowerCore_Fitness_Profile.pdf"
              className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-black text-sm text-gray-900 bg-white transition-all hover:scale-105 hover:shadow-2xl"
              style={{ letterSpacing: '0.06em' }}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Download Company Profile
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default QuickStart;